import { createElement, StrictMode } from 'react';
import { Cookies } from 'react-cookie';
import { createRoot, type Root } from 'react-dom/client';
import type { User } from 'oidc-client-ts';
import DarkLoadingSpinner from '../components/LoadingSpinner/DarkLoadingSpinner';
import { ROUTE_PATHS } from '../routes/routePaths';
import { isLoggedKey } from '../util/constants';
import { logger } from '../util/logger';
import { isEmbeddedMode } from './embedMode';
import { clearPartialAuth, setPartialAuthActive } from './partialAuthStorage';
import { getCurrentClientUserManager } from './userManager';
import { handOffExpiredSessionToParent } from './v3SessionBridge';

const SILENT_REAUTH_SPINNER_DOM_ID = 'silent-sso-reauth-spinner-root';

let reauthSpinnerRoot: Root | null = null;

function hideSilentSsoCheckingOverlay(): void {
	reauthSpinnerRoot?.unmount();
	reauthSpinnerRoot = null;
	document.querySelector(`#${SILENT_REAUTH_SPINNER_DOM_ID}`)?.remove();
}

function showSilentSsoCheckingOverlay(): void {
	hideSilentSsoCheckingOverlay();
	const container = document.createElement('div');
	container.id = SILENT_REAUTH_SPINNER_DOM_ID;
	document.body.append(container);
	reauthSpinnerRoot = createRoot(container);
	reauthSpinnerRoot.render(
		createElement(StrictMode, null, createElement(DarkLoadingSpinner)),
	);
}

let reauthInFlight: Promise<boolean> | null = null;

function shouldRedirectToTermsAfterSilentReauth(pathname: string): boolean {
	return pathname !== ROUTE_PATHS.TERMS_AND_CONDITIONS && pathname !== ROUTE_PATHS.OTP_VERIFICATION;
}

function syncTokensToLocalStorage(user: User | null): void {
	if (!user) return;
	if (user.access_token) localStorage.setItem('access_token', user.access_token);
	if (user.refresh_token) localStorage.setItem('refresh_token', user.refresh_token);
	if (user.token_type) localStorage.setItem('token_type', user.token_type);
	if (user.expires_in !== undefined) localStorage.setItem('expires_in', user.expires_in.toString());
	if (user.scope) localStorage.setItem('scope', user.scope);
	if (user.session_state) localStorage.setItem('session_state', user.session_state);
	if (user.id_token) localStorage.setItem('id_token', user.id_token);
}

function clearAppAuthStateBeforeReauth(): void {
	const cookies = new Cookies();
	cookies.remove(isLoggedKey, { path: '/' });
	cookies.remove('access_token', { path: '/' });
	cookies.remove('refresh_token', { path: '/' });
	clearPartialAuth();
}

export async function silentlyCheckKeycloakSessionAndRedirectToAppropriatePage(): Promise<boolean> {
	if (isEmbeddedMode()) {
		await handOffExpiredSessionToParent();
		return false;
	}

	if (reauthInFlight) {
		return reauthInFlight;
	}

	reauthInFlight = (async () => {
		showSilentSsoCheckingOverlay();
		clearAppAuthStateBeforeReauth();
		const { userManager: activeUserManager, clientId } = getCurrentClientUserManager();
		const isViewerClient = clientId === (process.env.REACT_APP_SECOND_CLIENT_ID ?? '');

		// Callback handler in Login uses this flag to choose viewerUserManager.
		if (isViewerClient) {
			sessionStorage.setItem('viewer_client_redirect', 'true');
		} else {
			sessionStorage.removeItem('viewer_client_redirect');
		}

		let navigatedAway = false;
		try {
			await activeUserManager.signinSilent({
				forceIframeAuth: true,
			});
			const freshUser = await activeUserManager.getUser();
			syncTokensToLocalStorage(freshUser);
			setPartialAuthActive(true);
			if (shouldRedirectToTermsAfterSilentReauth(window.location.pathname)) {
				navigatedAway = true;
				window.location.replace(ROUTE_PATHS.TERMS_AND_CONDITIONS);
			}
			return true;
		} catch (error) {
			logger.log('[OIDC] Iframe silent failed, redirecting', error);
			// Failure path: go to interactive login only (no Terms redirect).
			await activeUserManager.signinRedirect({
				extraQueryParams: {
					kc_error: 'SESSION_EXPIRED',
				},
			});
			navigatedAway = true;
			return false;
		} finally {
			if (!navigatedAway) {
				hideSilentSsoCheckingOverlay();
			}
		}
	})().finally(() => {
		reauthInFlight = null;
	});

	return reauthInFlight;
}

